"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CompanyAddress } from "@/app/(marketing)/contact/components/CompanyAddress";
import { PrivacyNotice } from "@/app/(marketing)/contact/components/PrivacyNotice";
import { StatusMessage } from "@/app/(marketing)/contact/components/StatusMessage";
import { Button } from "@/components/buttons/Button";
import { CheckboxField } from "@/components/form/CheckboxField";
import { InputField } from "@/components/form/InputField";
import { SelectField } from "@/components/form/SelectField";
import { TextareaField } from "@/components/form/TextareaField";
import { sendContactForm } from "@/data/contact/actions/sendContactForm";
import { contactFormSchema } from "@/types/defs/contactFormSchema";
import type { StatusType } from "@/types/types";
import { requiresAudioReviewConsent } from "@/utils/contact/requiresAudioReviewConsent";

export function ContactForm() {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      category: "general",
      name: "",
      email: "",
      message: "",
      technicalIssueType: undefined,
      consentToAudioReview: false,
    },
  });

  const category = watch("category");
  const technicalIssueType = watch("technicalIssueType");
  const [status, setStatus] = useState<StatusType>(null);
  const shouldShowConsentToAudioReview = requiresAudioReviewConsent({
    category,
    technicalIssueType,
  });

  const audioReviewDescription =
    "To accurately diagnose voice quality issues, our support team needs access to the input audio, output audio, and (if applicable) any associated model training audio related to this request. This audio is accessed solely for troubleshooting purposes and is not used for training or improving our models.";

  useEffect(() => {
    // When switching to "technical", make the first visible option ("voice_quality") a real form value so RHF state matches what the browser shows.
    if (category === "technical" && technicalIssueType === undefined) {
      setValue("technicalIssueType", "voice_quality", {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [category, setValue, technicalIssueType]);

  useEffect(() => {
    // If the user changes to a technical issue type that doesn't require consent, reset consent so it can't accidentally carry over.
    const shouldRequireConsent = requiresAudioReviewConsent({
      category,
      technicalIssueType,
    });

    if (!shouldRequireConsent) {
      setValue("consentToAudioReview", false, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [category, setValue, technicalIssueType]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (isSubmitSuccessful) {
      timeoutId = setTimeout(() => {
        reset({
          category: "general",
          name: "",
          email: "",
          message: "",
          technicalIssueType: undefined,
          consentToAudioReview: false,
        });
      }, 5000);
    }
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSubmitSuccessful, reset]);

  const onSubmit = async (data: z.infer<typeof contactFormSchema>) => {
    try {
      const response = await sendContactForm(data);
      if (response.success) {
        setStatus({ type: "success", message: "Message sent successfully!" });
      } else {
        setStatus({
          type: "error",
          message:
            response.error === undefined ?
              "Failed to send message"
            : response.error,
        });
      }
    } catch {
      setStatus({ type: "error", message: "Failed to send message" });
    }
  };

  return (
    <div className="space-y-8 rounded-lg">
      <StatusMessage status={status} />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <SelectField
          id="category"
          label="Inquiry Category"
          error={errors.category !== undefined ? errors.category.message : null}
          register={register("category")}
        >
          <option value="" disabled className="text-gray-500">
            Select a category for your inquiry
          </option>
          <option value="general">General Inquiry</option>
          <option value="technical">Technical Support</option>
          <option value="subscription">Subscription & Plans</option>
          <option value="billing">Billing & Payments</option>
          <option value="enterprise">Enterprise Inquiry</option>
          <option value="feedback">Feedback & Requests</option>
          <option value="other">Other</option>
        </SelectField>

        {category === "technical" && (
          <SelectField
            id="technicalIssueType"
            label="Technical Issue Type"
            error={
              errors.technicalIssueType !== undefined ?
                errors.technicalIssueType.message
              : null
            }
            register={register("technicalIssueType")}
          >
            <option value="" disabled className="text-gray-500">
              Select the type of technical issue
            </option>
            <option value="voice_quality">
              Voice Conversion Quality Issues
            </option>
            <option value="audio_file_format">
              Audio File & Format Issues
            </option>
            <option value="voice_model">Model Training Issues</option>
            <option value="platform_access">
              Platform Access/Login Issues
            </option>
            <option value="other">Other Technical Issue</option>
          </SelectField>
        )}

        {shouldShowConsentToAudioReview && (
          <CheckboxField
            id="consentToAudioReview"
            label="Audio review required for this request"
            description1={audioReviewDescription}
            description2="I allow the support team to review the audio associated with this request."
            error={
              errors.consentToAudioReview !== undefined ?
                errors.consentToAudioReview.message
              : null
            }
            register={register("consentToAudioReview")}
          />
        )}

        <InputField
          id="name"
          type="text"
          label="Full Name"
          placeholder="Enter your full name"
          error={errors.name !== undefined ? errors.name.message : null}
          register={register("name")}
        />

        <InputField
          id="email"
          type="email"
          label="Email Address"
          placeholder="Enter your email address (e.g., name@example.com)"
          error={errors.email !== undefined ? errors.email.message : null}
          register={register("email")}
        />

        <TextareaField
          id="message"
          label="Message"
          placeholder="Please describe how we can help you in detail"
          error={errors.message !== undefined ? errors.message.message : null}
          register={register("message")}
        />

        <Button
          type="submit"
          disabled={isSubmitting || isSubmitSuccessful}
          className="w-full"
          loading={isSubmitting}
        >
          {!isSubmitting && isSubmitSuccessful && <span>Message Sent</span>}
          {!isSubmitting && !isSubmitSuccessful && <span>Submit</span>}
        </Button>
      </form>

      <PrivacyNotice />
      <hr />
      <CompanyAddress />
    </div>
  );
}
